import { HitTestResult } from '@chartv2/engine/hit-test';
import { cloneElement, isValidElement, type ReactElement } from 'react';

import type {
  ChartV2LegendMoreItem,
  ChartV2ResolvedSeries,
  RenderLegendTooltip,
} from '../types/chart-v2.types';
import { CloseIcon, EyeIcon, EyeOffIcon } from './Icons';
import styles from './Legend.module.scss';
import { LegendMoreMenu } from './LegendMoreMenu';

export interface LegendItemEntry {
  series: ChartV2ResolvedSeries;
  hidden?: boolean;
  moreMenuItems?: ChartV2LegendMoreItem[];
}

export interface LegendItemLocales {
  more?: string;
  hide?: string;
  show?: string;
  remove?: string;
}

export interface LegendItemProps {
  entry: LegendItemEntry;
  onToggle?: (seriesId: string, isCurrentlyHidden: boolean) => void;
  onRemove?: (seriesId: string) => void;
  locales?: LegendItemLocales;
  hoverHit?: HitTestResult;
  renderTooltip?: RenderLegendTooltip;
}

/**
 * One row in the legend — label slot on the left, the consumer's
 * status node on the right (resting state), and a hover-only icon
 * group with the more-menu / hide / remove actions.
 */
export const LegendItem = ({
  entry,
  onToggle,
  onRemove,
  locales,
  hoverHit,
  renderTooltip,
}: LegendItemProps) => {
  const { series, hidden, moreMenuItems } = entry;
  const hasMoreMenu = (moreMenuItems?.length ?? 0) > 0;
  const toggleTitle = hidden
    ? (locales?.show ?? 'Show series')
    : (locales?.hide ?? 'Hide series');
  const removeTitle = locales?.remove ?? 'Remove series';
  const withTooltip = (
    trigger: ReactElement<Record<string, unknown>>,
    label: string
  ) => (renderTooltip ? renderTooltip(trigger, label) : trigger);

  const status =
    isValidElement(series.legendStatus) &&
    typeof series.legendStatus.type !== 'string'
      ? cloneElement(
          series.legendStatus as ReactElement<{ hoverHit?: HitTestResult }>,
          { hoverHit }
        )
      : series.legendStatus;

  return (
    <li
      className={styles.item}
      data-testid={`chartv2-legend-item-${series.id}`}
    >
      <div className={styles.itemDetails}>
        <div
          className={styles.itemHeader}
          data-testid={`chartv2-legend-label-${series.id}`}
        >
          {series.legendLabel ?? (
            <span className={styles.fallbackLabel}>{series.label}</span>
          )}
        </div>

        <div className={styles.hoverActions}>
          {hasMoreMenu && moreMenuItems && (
            <LegendMoreMenu
              items={moreMenuItems}
              seriesId={series.id}
              triggerTitle={locales?.more}
              renderTooltip={renderTooltip}
            />
          )}
          {onToggle &&
            withTooltip(
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => onToggle(series.id, !!hidden)}
                aria-label={toggleTitle}
                title={renderTooltip ? undefined : toggleTitle}
                data-testid={`chartv2-legend-toggle-${series.id}`}
              >
                {hidden ? <EyeOffIcon /> : <EyeIcon />}
              </button>,
              toggleTitle
            )}
          {onRemove &&
            withTooltip(
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => onRemove(series.id)}
                aria-label={removeTitle}
                title={renderTooltip ? undefined : removeTitle}
                data-testid={`chartv2-legend-remove-${series.id}`}
              >
                <CloseIcon />
              </button>,
              removeTitle
            )}
        </div>
      </div>

      {!!series.legendStatus && (
        <div
          className={styles.restingContent}
          data-testid={`chartv2-legend-status-${series.id}`}
        >
          {/* use SeriesLegendStatus component instead of cloneElement once chartv2 is merged */}
          {status}
        </div>
      )}
    </li>
  );
};
